import { Link, useNavigate } from 'react-router-dom'

import { api } from '../api/client'
import type { Credential, CredentialKind, Probe } from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { Connessioni } from '../components/Connessioni'
import { DataTable } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/** Il modulo di ogni funzione: lì si leggono i dati («Apri») e si gestiscono
 *  le credenziali (sezione «Connessioni»). ntopng non ha un modulo — si legge
 *  nella scheda del CI — e le sue connessioni si gestiscono in questa pagina. */

/** Le funzioni che hanno un modulo ma le cui connessioni si gestiscono **qui**:
 *  la pagina del modulo resta per lavorare sui dati (il DNS: zone e record),
 *  senza l'elenco delle credenziali in fondo. */
const CONNESSIONI_QUI = new Set(['dns'])
/** Sources that live in an area of their own: GitLab, Artifactory and Nexus
 *  (feature «sbom») are created and managed in DevSecOps → Connessioni. */
const ALTROVE: Record<string, string> = { sbom: '/software-supply/connections' }
const MODULO: Record<string, string> = {
  backup: '/backup',
  ipam: '/ipam',
  awx: '/awx',
  dns: '/dns',
  wazuh: '/wazuh',
  cynet: '/cynet',
  firewall: '/firewall',
}

/**
 * Le **sorgenti live** che alimentano CENSO — SIEM (Wazuh), traffico (ntopng),
 * firewall — raggruppate per prodotto, con stato e sonda. Sono credenziali di
 * tipo «live» (interrogate in tempo reale via sonda): qui le si vede in un colpo
 * d'occhio invece che sparse fra le credenziali. La pagina è data-driven dal
 * catalogo: un prodotto nuovo con `live=true` compare da solo.
 */
export default function Integrazioni() {
  const kinds = useAsync(() => api.credentialKinds(), [])
  const credenziali = useAsync(() => api.credentials(), [])
  const license = useAsync(() => api.license(), [])
  const probes = useAsync(() => api.probes(), [])

  const caricamento = kinds.loading || credenziali.loading
  const errore = kinds.error || credenziali.error
  const live = (kinds.data ?? []).filter((k) => k.live && !ALTROVE[k.feature])
  const devsecops = (kinds.data ?? []).filter((k) => k.live && k.feature === 'sbom')
  const features = license.data?.features ?? {}
  const perSonda = new Map((probes.data ?? []).map((p) => [p.id, p] as [string, Probe]))

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Sorgenti dati')}</h1>
          <p className="page__subtitle">
            {t('Catalogo centralizzato delle sorgenti dati per rete, virtualizzazione, storage, sicurezza, backup e DNS. Mostra per ciascuna sorgente lo stato operativo, connessa, non configurata o in errore, e le credenziali associate.')}
          </p>
        </div>
      </header>

      <ErrorBanner error={errore} />
      {devsecops.length > 0 && (
        <div className="banner banner--info">
          {t('{nomi} si collegano dall’area DevSecOps:', { nomi: devsecops.map((k) => t(k.label)).join(', ') })}{' '}
          <Link to={ALTROVE.sbom}>{t('DevSecOps → Connessioni')}</Link>
        </div>
      )}

      {caricamento && !kinds.data ? (
        <Spinner />
      ) : live.length === 0 ? (
        <EmptyState
          title={t('Nessuna sorgente disponibile.')}
          hint={t('Le sorgenti live (Wazuh, ntopng…) compaiono qui appena il catalogo le prevede.')}
        />
      ) : (
        <Card>
          <TabellaSorgenti
            sorgenti={live}
            credenziali={credenziali.data ?? []}
            probes={perSonda}
            licenziata={(k) => features[k.feature] !== false}
          />
        </Card>
      )}

      {live.some((k) => !MODULO[k.feature] || CONNESSIONI_QUI.has(k.feature)) && (
        <>
          <h2 className="sezione-pagina__titolo" id="connessioni">{t('Connessioni')}</h2>
          <Connessioni
            funzioni={[...new Set(live.filter((k) => !MODULO[k.feature] || CONNESSIONI_QUI.has(k.feature)).map((k) => k.feature))]}
            onChanged={() => credenziali.reload()}
          />
        </>
      )}
    </>
  )
}

/** The family of a source, for the «Tipo» column and its filter. */
function tipoSorgente(feature: string): string {
  const tipi: Record<string, string> = {
    firewall: t('Firewall'), backup: t('Backup'), dns: t('DNS'), ipam: t('IPAM'),
    wazuh: t('SIEM'), cynet: t('EDR/XDR'), ntopng: t('Traffico'), awx: t('Automazione'),
  }
  return tipi[feature] ?? feature
}

/**
 * One row per source (1/11/2026: the cards of different heights, with wrapped
 * titles and stacked buttons, were «troppo disordinate»): type, state, the
 * credentials with their probe, and the actions on one line — configured
 * sources first.
 */
function TabellaSorgenti({
  sorgenti,
  credenziali,
  probes,
  licenziata,
}: {
  sorgenti: CredentialKind[]
  credenziali: Credential[]
  probes: Map<string, Probe>
  licenziata: (k: CredentialKind) => boolean
}) {
  const nav = useNavigate()
  const di = (k: CredentialKind) => credenziali.filter((c) => c.kind === k.kind)
  const sondaDi = (c: Credential): string => {
    const id = String((c.meta as Record<string, unknown>)?.appliance_id ?? '')
    return (id && probes.get(id)?.name) || t('la prima idonea')
  }
  const stato = (k: CredentialKind): string => {
    if (!licenziata(k)) return t('Non compreso nella licenza')
    const n = di(k).length
    return n === 0 ? t('da configurare') : n === 1 ? t('configurato') : t('{n} configurati', { n })
  }
  const ordinate = [...sorgenti].sort((a, b) =>
    Number(di(b).length > 0) - Number(di(a).length > 0) || t(a.label).localeCompare(t(b.label)))
  return (
    <DataTable
      rows={ordinate}
      rowKey={(k) => k.kind}
      conteggio={{ nome: t('sorgenti') }}
      columns={[
        { key: 'label', header: t('Sorgente'), value: (k) => t(k.label), render: (k) => <strong>{t(k.label)}</strong> },
        { key: 'tipo', header: t('Tipo'), filter: 'select', value: (k) => tipoSorgente(k.feature) },
        {
          key: 'stato', header: t('Stato'), filter: 'select', value: (k) => stato(k),
          render: (k) => {
            const n = di(k).length
            const tono = !licenziata(k) ? 'warn' : n > 0 ? 'ok' : 'muted'
            return <span className={`badge badge--${tono}`}>{stato(k)}</span>
          },
        },
        {
          key: 'credenziali', header: t('Credenziali e sonda'),
          value: (k) => di(k).map((c) => `${c.name} ${sondaDi(c)}`).join(' '),
          render: (k) => di(k).length === 0 ? <span className="muted">—</span> : (
            <ul className="sorgenti__credenziali">
              {di(k).map((c) => (
                <li key={c.id}>{c.name} <span className="muted">· {sondaDi(c)}</span></li>
              ))}
            </ul>
          ),
        },
        {
          key: 'azioni', header: '', filter: false, sort: false, searchable: false,
          render: (k) => {
            const modulo = MODULO[k.feature]
            const configura = modulo && !CONNESSIONI_QUI.has(k.feature) ? `${modulo}#connessioni` : '#connessioni'
            const n = di(k).length
            return (
              <div className="rowactions">
                {modulo && n > 0 && <button type="button" onClick={() => nav(modulo)}>{t('Apri')}</button>}
                {licenziata(k) && (
                  <button type="button" className="secondary" onClick={() => {
                    // Same page (ntopng, DNS): bring the section into view.
                    if (configura.startsWith('#')) document.getElementById('connessioni')?.scrollIntoView()
                    else nav(configura)
                  }}>
                    {n > 0 ? t('Modifica') : t('Configura')}
                  </button>
                )}
              </div>
            )
          },
        },
      ]}
    />
  )
}
